import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Divider } from '@mui/material';
import { ReactComponent as ChevronRightIcon } from '../../../assets/icons/chevron-right.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../assets/icons/chevron-left.svg';
import { getStatusForRangeDate } from '../../../utils/general';
import {
  mainColors,
  secondaryColors,
  textColors
} from '../../../theme/themeConfig';

export interface StateDatesList {
  formatted: string;
  start: string;
  state: string;
}
export interface Dates {
  days: string[];
  id: number;
  range: string;
  stateDatesList: StateDatesList[];
  status: string;
}

function DateToSelect({
  date,
  handleClick,
  id,
  selected,
  status
}: {
  date: string;
  handleClick: (key: number) => void;
  id: number;
  selected: number;
  status: any;
}) {
  const isSelected = selected === id;
  const BGColor = isSelected ? '#1a3055' : status.bg;
  const textColor = isSelected ? '#ffffff' : status.txt;
  const borderColor = isSelected ? '#1a3055' : status.txt;

  return (
    <Box
      onClick={() => handleClick(id)}
      sx={{
        alignItems: 'center',
        backgroundColor: BGColor,
        '&:hover': {
          backgroundColor: isSelected ? mainColors.MAIN : `${status.bg}CC`
        },
        borderBottom: isSelected
          ? `3px solid rgba(255,255,255,0.4)`
          : `3px solid transparent`,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        justifyContent: 'center',
        minWidth: '180px',
        padding: '10px 8px',
        transition: 'background-color 0.15s',
        width: '100%'
      }}
    >
      {/* Status dot */}
      <Box
        sx={{
          backgroundColor: isSelected ? 'rgba(255,255,255,0.7)' : borderColor,
          borderRadius: '50%',
          height: 6,
          width: 6
        }}
      />
      <Typography
        sx={{
          color: textColor,
          fontSize: '11px',
          fontWeight: isSelected ? 800 : 600,
          lineHeight: '16px',
          textAlign: 'center',
          width: '100%'
        }}
      >
        {date}
      </Typography>
    </Box>
  );
}

interface DatesProps {
  dates: Dates[];
  selected: number;
  setDay: React.Dispatch<React.SetStateAction<any>>;
  setDetailInfo: React.Dispatch<React.SetStateAction<{}>>;
  setSelectedDayIndex: React.Dispatch<React.SetStateAction<number>>;
}

const CarrouseDates: React.FC<DatesProps> = function CarrouseDates({
  selected,
  dates,
  setSelectedDayIndex,
  setDay,
  setDetailInfo
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const visibleCount = 5; // Number of dates visible at a time
  const showArrows = dates.length > visibleCount;

  useEffect(() => {
    const today = new Date();

    const index = dates.findIndex((date: Dates) => {
      const [startDateStr] = date.range.split(' - ');
      const startDate = new Date(startDateStr);
      return startDate > today;
    });

    if (index !== -1) {
      const initialIndex = Math.max(0, index - Math.floor(visibleCount / 2));
      setCurrentIndex(initialIndex);
    } else {
      setCurrentIndex(Math.max(0, dates.length - visibleCount));
    }
  }, [dates]);

  useEffect(() => {
    const selectedIndex = dates.findIndex(
      (date: Dates) => date.id === selected
    );
    if (selectedIndex !== -1) {
      if (
        selectedIndex < currentIndex ||
        selectedIndex >= currentIndex + visibleCount
      ) {
        const newIndex = Math.max(
          0,
          selectedIndex - Math.floor(visibleCount / 2)
        );
        setCurrentIndex(newIndex);
      }
    }
  }, [selected, dates, currentIndex, visibleCount]);

  const handleClick = (key: number) => {
    setSelectedDayIndex(key);
    setDay(null);
    setDetailInfo({});
  };

  const handlerCountRight = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + visibleCount < dates.length ? prevIndex + 1 : prevIndex
    );
  };

  const handlerCountLeft = () => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: `1px solid ${secondaryColors.SECONDARY_1}`,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        width: '100%'
      }}
    >
      {showArrows && (
        <Box
          sx={{
            alignItems: 'center',
            borderRight: `1px solid ${secondaryColors.SECONDARY_1}`,
            display: 'flex',
            justifyContent: 'center',
            opacity: currentIndex === 0 ? 0.3 : 1,
            width: '40px'
          }}
        >
          <Button
            disabled={currentIndex === 0}
            onClick={handlerCountLeft}
            size="small"
            sx={{ height: '100%', minWidth: 0, width: '40px' }}
          >
            <ChevronLeftIcon />
          </Button>
        </Box>
      )}

      <Box
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'row'
        }}
      >
        {dates
          .slice(currentIndex, currentIndex + visibleCount)
          .map((dateProps, index, visibleDates) => {
            const status = getStatusForRangeDate(dateProps.status);
            const isLast = index === visibleDates.length - 1;
            return (
              <>
                <DateToSelect
                  date={dateProps.range}
                  handleClick={handleClick}
                  id={dateProps.id}
                  key={dateProps.id}
                  selected={selected}
                  status={status}
                />
                {!isLast && (
                  <Divider
                    flexItem
                    orientation="vertical"
                    sx={{ borderColor: secondaryColors.SECONDARY_1 }}
                  />
                )}
              </>
            );
          })}
      </Box>

      {showArrows && (
        <Box
          sx={{
            alignItems: 'center',
            borderLeft: `1px solid ${secondaryColors.SECONDARY_1}`,
            display: 'flex',
            justifyContent: 'center',
            opacity: currentIndex + visibleCount >= dates.length ? 0.3 : 1,
            width: '40px'
          }}
        >
          <Button
            disabled={currentIndex + visibleCount >= dates.length}
            onClick={handlerCountRight}
            size="small"
            sx={{ height: '100%', minWidth: 0, width: '40px' }}
          >
            <ChevronRightIcon />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CarrouseDates;
